import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  Hidden,
  IconChevron,
  Secondary,
} from 'braid-design-system';
import type { FC, PropsWithChildren } from 'react';

import type { PaginationButtonType } from 'src/utils/events';

import translations from './.vocab';

import * as styles from './Pagination.css';

interface PaginationButtonProps {
  active?: boolean;
  onClick?: () => void;
  dataCy?: string;
  dataTestId?: string;
  parentStyles?: string;
}

const PaginationButton: FC<PropsWithChildren<PaginationButtonProps>> = ({
  children,
  active,
  onClick,
  parentStyles = '',
  dataCy,
  dataTestId,
}) => (
  <Box component="div" className={[styles.button, parentStyles]}>
    <Button
      onClick={onClick}
      variant={active ? 'soft' : 'transparent'}
      data={{
        ...(dataCy && { cy: dataCy }),
        ...(dataTestId && { testid: dataTestId }),
      }}
    >
      {active && children}
      {!active && <Secondary>{children}</Secondary>}
    </Button>
  </Box>
);

const PaginationEllipsisBox: FC = ({}) => (
  <PaginationButton parentStyles={styles.ellipse}>…</PaginationButton>
);

interface PaginationNextPreviousProps {
  onClick: () => void;
}

const PaginationPrevious: FC<PaginationNextPreviousProps> = ({ onClick }) => {
  const { t } = useTranslations(translations);
  return (
    <PaginationButton onClick={onClick}>
      <Box display="flex">
        <Box>
          <IconChevron direction="left" tone="secondary" />
        </Box>
        <Hidden below="tablet">
          <Box paddingLeft="small">{t('Previous')}</Box>
        </Hidden>
      </Box>
    </PaginationButton>
  );
};

const PaginationNext: FC<PaginationNextPreviousProps> = ({ onClick }) => {
  const { t } = useTranslations(translations);
  return (
    <PaginationButton onClick={onClick} dataCy="page-button-next">
      <Box display="flex">
        <Hidden below="tablet">
          <Box paddingRight="small">{t('Next')}</Box>
        </Hidden>
        <Box>
          <IconChevron direction="right" tone="secondary" />
        </Box>
      </Box>
    </PaginationButton>
  );
};

interface Props {
  totalPages: number;
  currentPage: number;
  handlePageChange: (page: number, buttonType: PaginationButtonType) => void;
  isMobile: boolean;
}

const isEndPages = (pageNumber: number, totalPages: number) =>
  pageNumber === 1 || pageNumber === totalPages;

const createDesktopThresholds = () => ({
  eitherSideThreshold: 2,
  startThreshold: 5,
  displayAllThreshold: 7,
  ellipseThreshold: 3,
});

const createMobileThresholds = () => ({
  eitherSideThreshold: 1,
  startThreshold: 2,
  displayAllThreshold: 5,
  ellipseThreshold: 1,
});

const Pagination: FC<Props> = ({
  currentPage,
  totalPages,
  handlePageChange,
  isMobile,
}) => {
  if (totalPages <= 1) {
    return <></>;
  }

  const {
    displayAllThreshold,
    eitherSideThreshold,
    ellipseThreshold,
    startThreshold,
  } = isMobile ? createMobileThresholds() : createDesktopThresholds();

  const showPrevious = currentPage > 1;
  const showNext = currentPage < totalPages;

  const shouldDisplayAll = totalPages < displayAllThreshold;

  const isCloseToStart = (currentPageNumber: number, pageNumber: number) =>
    currentPageNumber < startThreshold && pageNumber < startThreshold;

  const isCloseToEnd = (
    currentPageNumber: number,
    pageNumber: number,
    totalNumberOfPages: number,
  ) =>
    currentPageNumber > totalNumberOfPages - (startThreshold - 1) &&
    pageNumber > totalNumberOfPages - (startThreshold - 1);

  const pageNumbers = Array.from({ length: totalPages }).map((_, i) => i + 1);

  const hasEllipsisBeforeCurrentPage =
    !shouldDisplayAll && currentPage - 1 > ellipseThreshold;
  const hasEllipsisAfterCurrentPage =
    !shouldDisplayAll && currentPage < totalPages - ellipseThreshold;

  const pageNumbersToDisplay = pageNumbers.filter((pageNumber) => {
    if (isEndPages(pageNumber, totalPages)) {
      return false;
    }

    if (shouldDisplayAll) {
      return true;
    }

    if (isCloseToStart(currentPage, pageNumber)) {
      return true;
    }
    if (isCloseToEnd(currentPage, pageNumber, totalPages)) {
      return true;
    }
    return Math.abs(pageNumber - currentPage) < eitherSideThreshold;
  });

  return (
    <Box background="surface" paddingX="medium" paddingY="small" display="flex">
      {showPrevious && (
        <PaginationPrevious
          onClick={() => handlePageChange(currentPage - 1, 'previous')}
        />
      )}
      <PaginationButton
        active={currentPage === 1}
        onClick={() => handlePageChange(1, 'number')}
        dataTestId="page-number-one"
      >
        1
      </PaginationButton>
      {hasEllipsisBeforeCurrentPage && <PaginationEllipsisBox />}
      {pageNumbersToDisplay.map((pageNumber) => (
        <PaginationButton
          key={pageNumber}
          active={currentPage === pageNumber}
          onClick={() => handlePageChange(pageNumber, 'number')}
        >
          {pageNumber}
        </PaginationButton>
      ))}
      {hasEllipsisAfterCurrentPage && <PaginationEllipsisBox />}
      {totalPages > 1 && (
        <PaginationButton
          active={currentPage === totalPages}
          onClick={() => handlePageChange(totalPages, 'number')}
        >
          {totalPages}
        </PaginationButton>
      )}
      {showNext && (
        <PaginationNext
          onClick={() => handlePageChange(currentPage + 1, 'next')}
        />
      )}
    </Box>
  );
};

export default Pagination;
